import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { filterArrayEmpty } from 'lib/riskbase';
import { pluraliseS } from 'lib/pluralise';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { CommunicationType, EngagePropertyListItem } from 'types/Engage';
import type { FlatIssues } from '../EngageSendCommunicationModal';
import {
  EngageScheduledVisitRecipient,
  EngageScheduledVisitRecipientMessageDeliverabilityStatus as DeliverabilityStatus,
} from 'types/DTOs/BlockManagerDTOs';
import { Pill } from 'components/Pill';

export type EngageCommunicationCalculatedFlat = {
  // Allow callers that only know id/name (e.g. scheduled visit recipients) as well
  flat: { id: string; name: string };
  bulkSendIssues?: FlatIssues;
  scheduledVisitIssues?: string;
  deliverabilityStatusReason?: EngageScheduledVisitRecipient['deliverabilityStatusReason'];
  deliverabilityStatus?: EngageScheduledVisitRecipient['deliverabilityStatus'];
};

type Props = {
  property: EngagePropertyListItem;
  statusGroups: Record<
    DeliverabilityStatus,
    EngageCommunicationCalculatedFlat[]
  >;
  communicationType?: CommunicationType;
  isBulkMode?: boolean;
  flatCount?: number;
  loading?: boolean;
  scheduledVisitLabels?: Partial<Record<DeliverabilityStatus, string>>;
};

type StatusKey = DeliverabilityStatus;

const statusOrder: StatusKey[] = [
  DeliverabilityStatus.ERROR,
  DeliverabilityStatus.WARNING,
  DeliverabilityStatus.OKAY,
];
const statusIconColour: Record<
  StatusKey,
  { icon?: ICONS; color?: string | undefined }
> = {
  error: {
    icon: ICONS.TriangleExclamationDuotone,
    color: theme.colors.red,
  },
  warning: {
    icon: ICONS.TriangleExclamationDuotone,
    color: theme.colors.orange,
  },
  okay: {
    icon: undefined,
    color: undefined,
  },
};

export const EngageSendCommunicationFlatSummary: React.FC<Props> = ({
  property,
  statusGroups,
  communicationType,
  isBulkMode,
  flatCount,
  loading = false,
  scheduledVisitLabels,
}) => {
  const hasFlats = !!flatCount;

  const labelForStatus = (status: StatusKey, count: number): string => {
    const scheduledVisitLabel = scheduledVisitLabels?.[status];
    if (scheduledVisitLabel) {
      return scheduledVisitLabel;
    }

    if (status === 'error') {
      return pluraliseS(count, 'Flat') + ' Cannot Be Emailed';
    }
    if (status === 'warning') {
      return pluraliseS(count, 'Flat') + ' Will Be Emailed With Warnings';
    }
    if (status === 'okay') {
      return communicationType === CommunicationType.email && isBulkMode
        ? pluraliseS(count, 'Flat') + ' Will Be Emailed'
        : communicationType === CommunicationType.email
          ? 'Flat'
          : `Send To ${pluraliseS(count, 'Flat')}`;
    }
    return pluraliseS(count, 'Flat') + ' Will Be Messaged';
  };

  return (
    <>
      {loading && !hasFlats && (
        <View
          style={{
            paddingVertical: theme.paddingHorizontal,
            alignItems: 'center',
          }}
        >
          <ActivityIndicator />
          <Txt
            size="small"
            style={{ color: theme.colors.fieldValueText, marginTop: 6 }}
          >
            Fetching recipient list...
          </Txt>
        </View>
      )}
      {statusOrder.map((status) => {
        const flats = statusGroups[status];
        if (!flats.length) {
          return null;
        }
        return (
          <Card
            key={status}
            label={labelForStatus(status, flats.length)}
            labelIcon={statusIconColour[status].icon}
            labelColor={statusIconColour[status].color}
          >
            {flats.map((calculatedFlat) => (
              <FlatRow
                key={calculatedFlat.flat.id}
                property={property}
                calculatedFlat={calculatedFlat}
                addressNumberOfLines={flatCount ? 2 : undefined}
              />
            ))}
          </Card>
        );
      })}

      {!loading && !hasFlats && (
        <Txt
          size="small"
          style={{
            color: theme.colors.fieldValueText,
            marginTop: theme.paddingHorizontal,
            marginHorizontal: theme.paddingHorizontal,
          }}
        >
          No recipients to display.
        </Txt>
      )}
    </>
  );
};

const FlatRow: React.FC<{
  property: EngagePropertyListItem;
  calculatedFlat: EngageCommunicationCalculatedFlat;
  addressNumberOfLines: number | undefined;
}> = ({ property, calculatedFlat, addressNumberOfLines }) => {
  // Extracted from EngageSendCommunicationModal:
  // additional 'bulkSendIssues' and ragColour for Scheduled Visits
  const { flat, bulkSendIssues, scheduledVisitIssues, deliverabilityStatus } =
    calculatedFlat;
  const warningColor = theme.colors.orange;
  const errorColor = theme.colors.red;
  const ragColor = deliverabilityStatus
    ? {
        error: theme.colors.red,
        warning: theme.colors.orange,
        okay: theme.colors.green,
      }[deliverabilityStatus]
    : undefined;
  // Scheduled visit recipients use deliverabilityStatus; send communication uses bulkSendIssues
  const errorLevel =
    bulkSendIssues?.errorLevel ??
    (deliverabilityStatus === 'error'
      ? 2
      : deliverabilityStatus === 'warning'
        ? 1
        : 0);
  return (
    <CardRow
      key={flat.id}
      childrenStyle={{
        flexDirection: 'row',
        columnGap: theme.paddingHorizontal,
        alignItems: 'flex-start',
      }}
    >
      <View
        style={{
          borderRadius: 42,
          width: 42,
          height: 42,
          alignItems: 'center',
          justifyContent: 'center',
          borderColor:
            errorLevel === 2
              ? theme.colors.red50
              : errorLevel === 1
                ? theme.colors.orange50
                : undefined,
          borderWidth: errorLevel ? 2 : 0,
          backgroundColor: theme.colors.black5,
        }}
      >
        <Icon source={ICONS.HouseBuildingLight} />
      </View>
      <View style={{ flex: 1 }}>
        <Txt numberOfLines={addressNumberOfLines}>
          <Txt weight="bold">{flat.name}</Txt>
          {!!property && (
            <Txt size="small">
              ,{' '}
              {[...(property.addressLines || []), property.postcode]
                .filter(filterArrayEmpty)
                .join(', ')}
            </Txt>
          )}
        </Txt>
        <View
          style={{
            alignSelf: 'flex-start',
            flexDirection: 'column',
            gap: theme.paddingHorizontalHalf,
          }}
        >
          {scheduledVisitIssues ? (
            <Pill
              withIcon={true}
              iconColor={ragColor}
              label={scheduledVisitIssues}
            />
          ) : (
            <>
              {(bulkSendIssues?.noOwners ||
                bulkSendIssues?.noOwnersWithEmails) && (
                <Pill
                  withIcon={true}
                  iconColor={errorColor}
                  label={
                    `No owners` +
                    (bulkSendIssues?.noOwnersWithEmails
                      ? ' with email addresses'
                      : '')
                  }
                />
              )}
              {(bulkSendIssues?.noResidents ||
                bulkSendIssues?.noResidentsWithEmails) && (
                <Pill
                  withIcon={true}
                  iconColor={errorColor}
                  label={
                    `No residents` +
                    (bulkSendIssues?.noResidentsWithEmails
                      ? ' with email addresses'
                      : '')
                  }
                />
              )}
              {bulkSendIssues?.missingPrimaryOwners && (
                <Pill
                  withIcon={true}
                  iconColor={warningColor}
                  label={`No primary owners - emailing all owners`}
                />
              )}
              {bulkSendIssues?.missingPrimaryResidents && (
                <Pill
                  withIcon={true}
                  iconColor={warningColor}
                  label={`No primary residents - emailing all residents`}
                />
              )}
            </>
          )}
        </View>
      </View>
    </CardRow>
  );
};
